import { i18n } from '@/i18n/i18n'
import Configuration from './Configuration.js'
import ConfigurationDashboard from './ConfigurationDashboard.js'
import L from 'lodash'
import markdownToTxt from 'markdown-to-txt'
import store from '@/store'
import Vue from 'vue'
import jsonLogic from '@/plugins/jsonLogic'
import { format } from '@/plugins/moment'

const DEFAULT_ITEM_VALUE = 'id'
const DEFAULT_ITEM_TEXT = 'text'

const TABLE_VIEW_VALUE = 'table'
const KANBAN_VIEW_VALUE = 'kanban'
const DASHBOARD_VIEW_VALUE = 'dashboard'

export default {

  getApplications () {
    if (store.getters['configurationStore/configurationLoadingCompleted']) {
      return store.getters['configurationStore/applications']
    }
    return {}
  },

  getTheme () {
    if (store.getters['configurationStore/configurationLoadingCompleted']) {
      return store.getters['configurationStore/theme']
    }
    return 'default'
  },

  getApplication (applicationId) {
    const apps = this.getApplications()
    return apps?.[applicationId]
  },

  getApplicationDefaultTab (applicationId, workspaceId) {
    const tabs = this.getApplicationTabs(applicationId, workspaceId)
    return tabs[0]?.id
  },

  getApplicationTabs (applicationId, workspaceId) {
    const tabs = []
    // const userIsAdmin = this.auth.userHasPrivilege('/admin/*')

    const labelDashboard = Configuration.i18n.overrideLabel(applicationId, 'label.workspaceOverview')
    const labelTracker = Configuration.i18n.overrideLabel(applicationId, 'label.workspaceTrackers')

    if (workspaceId) { // WOrkspace tabs
      const workspace = Configuration.auth.findWorkspace(applicationId, workspaceId)
      // const workspaceDashboard = Configuration.dashboard.getWorkspaceDashboard(applicationId, workspaceId)
      const userDashboard = Configuration.auth.getDashboardIds(applicationId, workspaceId)

      if (userDashboard && userDashboard.length > 0) tabs.push({ id: 'dashboard', component: 'DashboardTabs', name: labelDashboard })
      if (workspace?.trackers?.length > 0) tabs.push({ id: 'tracker', component: 'TrackerTabs', name: labelTracker })
      // if (userIsAdmin) tabs.push({ id: 'settings', component: 'WorkspaceSettings', name: 'Settings' })

      if (this.auth.userHasPrivilege(`/${applicationId}/workspace/${workspaceId}/report`)) {
        const reports = Configuration.auth.getReportIds(applicationId, workspaceId)
        const labelReport = Configuration.i18n.overrideLabel(applicationId, 'label.workspaceReport')
        if (reports && reports.length > 0) {
          tabs.push({ id: 'report', component: 'ReportTabs', name: labelReport })
        }
      }

      if (this.auth.userHasPrivilege(`/${applicationId}/workspace/${workspaceId}/document`)) {
        const docs = Configuration.document.getAvailableDocuments(applicationId, workspaceId)
        const labelDocument = Configuration.i18n.overrideLabel(applicationId, 'label.workspaceDocument')
        if (docs && docs.length > 0) {
          tabs.push({ id: 'document', component: 'DocumentTabs', name: labelDocument })
        }
      }
    } else { // Application tabs
      const applicationDashboard = ConfigurationDashboard.getApplicationDashboard(applicationId, workspaceId)
      if (applicationDashboard) tabs.push({ id: 'dashboard', component: 'Dashboard', name: labelDashboard })
      // if (userIsAdmin) tabs.push({ id: 'settings', component: 'AppSettings', name: 'Settings' })
    }

    return tabs
  },

  getTrackerType (applicationId, trackerTypeId) {
    let trackerType
    let application = this.getApplication(applicationId)
    if (application) {
      trackerType = application.trackerTypes?.[trackerTypeId]
    }
    return trackerType
  },

  getSetting (applicationId, settingId) {
    let application = this.getApplication(applicationId)
    return application?.settings?.[settingId]
  },

  getApplicationBaseURL (applicationId) {
    return this.getApplication(applicationId)
      ? this.getApplication(applicationId).baseURL
      : undefined
  },

  getNestedWorkspaces (applicationId, inWorkspaces, outWorkspaces) {
    try {
      for (const workspaceName in inWorkspaces) {
        const workspaceId = inWorkspaces[workspaceName].id
        outWorkspaces[workspaceId] = inWorkspaces[workspaceName]
        // traverse nested workspaces
        if (inWorkspaces[workspaceName].workspaces) {
          this.getNestedWorkspaces(
            applicationId, inWorkspaces[workspaceName].workspaces, outWorkspaces)
        }
      }
    } catch (e) {
      Vue.prototype.$debug(`getNestedWorkspaces error: ${e}`)
      return {}
    }
    // return all workspaces nested inside inWorkspaces

    return outWorkspaces
  },

  addContext (selectedContext, item) {
    Object.keys(item).map(key => {
      if (item[key] && typeof item[key] === 'string' && item[key].includes('$')) {
        item[key] = selectedContext[item[key].substring(1)]
      }
      if (item[key] && typeof item[key] === 'object') {
        return this.addContext(selectedContext, item[key])
      }
    })
    return item
  },

  /* the data table configuration api */

  datatable: {
    // Specific render methods for field types
    DEFAULT_RENDER_METHODS: {
      select: 'renderFieldSelect',
      date: 'renderFieldDate',
      datetime: 'renderFieldDateTime',
      tag: 'renderTags'
    },

    DEFAULT_SEARCH_METHODS: {
      select: 'searchSelectFields',
      tag: 'searchTagsFields'
    },

    DEFAULT_AVAILABLE_VIEWS: {
      [TABLE_VIEW_VALUE]: { text: 'label.tableView', value: TABLE_VIEW_VALUE },
      [KANBAN_VIEW_VALUE]: { text: 'label.kanbanView', value: KANBAN_VIEW_VALUE },
      [DASHBOARD_VIEW_VALUE]: { text: 'label.dashboardView', value: DASHBOARD_VIEW_VALUE }
    },

    DEFAULT_VIEWS_ICONS: {
      [TABLE_VIEW_VALUE]: 'mdi-table',
      [KANBAN_VIEW_VALUE]: 'mdi-cards-variant',
      [DASHBOARD_VIEW_VALUE]: 'mdi-view-dashboard'
    },

    DEFAULT_VIEW: TABLE_VIEW_VALUE,
    TABLE_VIEW_VALUE: TABLE_VIEW_VALUE,
    KANBAN_VIEW_VALUE: KANBAN_VIEW_VALUE,
    DASHBOARD_VIEW_VALUE: DASHBOARD_VIEW_VALUE,

    getDatatable ({
      applicationId,
      trackerTypeId,
      workspaceId
    } = {}) {
      // Base datatable defined at tracker o application level
      const trackerType = Configuration.getTrackerType(applicationId, trackerTypeId)
      let dataTable = trackerType?.dataTable
        ? trackerType.dataTable
        : Configuration.getApplication(applicationId).dataTable

      return this.getDatatableOverride(dataTable, applicationId, workspaceId, trackerTypeId)
    },

    getDatatableOverride (dataTable, applicationId, workspaceId, trackerTypeId) {
      // Check for workspace overrides in incremental way
      // ie "sorintlab.opportunity" il check first in "sorintlab" then "sorintlab.opportunity"
      if (workspaceId) {
        const workspaceTokens = workspaceId.split('.')
        const override = workspaceTokens.reduce((acc, _w, index) => {
          const workspace = Configuration.auth.findWorkspace(
            applicationId,
            workspaceTokens.slice(0, index + 1).join('.'),
            false // skip check of permissions
          )

          if (workspace?.dataTableOverride?.[trackerTypeId]) {
            return L.merge(acc, workspace.dataTableOverride[trackerTypeId])
          }
          return acc
        }, {})

        dataTable = L.merge({}, dataTable, override)
      }
      return dataTable
    },

    getSettingDatatable ({
      applicationId,
      settingId
    } = {}) {
      // Base datatable defined at tracker o application level
      const setting = Configuration.getSetting(applicationId, settingId)
      let dataTable = setting?.dataTable
        ? setting.dataTable
        : Configuration.getApplication(applicationId).dataTable

      return dataTable
    },

    sortDataTableHeaders (headers, columnsOrder = []) {
      if (Array.isArray(columnsOrder) && columnsOrder.length > 0) {
        const hhToSort = []
        const hhNotSorted = []

        headers.forEach(h => {
          const index = columnsOrder.indexOf(h.value)
          index >= 0 ? hhToSort.push(h) : hhNotSorted.push(h)
        })

        const hhSorted = hhToSort.sort((a, b) => {
          const indexA = columnsOrder.indexOf(a.value)
          const indexB = columnsOrder.indexOf(b.value)

          return indexA > indexB ? 1 : -1
        })

        return [
          ...hhSorted,
          ...hhNotSorted
        ]
      }

      return headers
    },

    getHeaders (dataTableConfig) {
      let dataTableHeaders = []
      if (dataTableConfig?.columns) {
        for (const colConfig of Object.values(dataTableConfig.columns)) {
          if (colConfig.header) {
            let header = {}
            header.value = colConfig.id
            header.text = colConfig.label ? i18n.t(colConfig.label) : colConfig.id
            header.align = (colConfig.align ? colConfig.align : (colConfig.type === 'number' ? 'end' : undefined))
            header.sortable = colConfig.hasOwnProperty('sortable') ? colConfig.sortable : true
            dataTableHeaders.push(header)
          }
        }
      }
      return this.sortDataTableHeaders(dataTableHeaders, dataTableConfig?.columnsOrder)
    },

    hasValidationRules (colConfig) {
      if (colConfig?.header?.rules || colConfig?.header?.veeRules) {
        return true
      }
      return false
    },

    getAvailableHeaders (dataTableConfig) {
      let dataTableHeaders = []
      if (dataTableConfig?.columns) {
        for (const colConfig of Object.values(dataTableConfig.columns)) {
          if (this.isAvailableHeader(colConfig)) {
            let header = {}
            header.value = colConfig.id
            header.text = colConfig.label ? i18n.t(colConfig.label) : colConfig.id
            header.align = (colConfig.align ? colConfig.align : (colConfig.type === 'number' ? 'end' : undefined))
            header.sortable = colConfig.hasOwnProperty('sortable') ? colConfig.sortable : true
            dataTableHeaders.push(header)
          }
        }
      }
      return dataTableHeaders
    },

    isAvailableHeader (colConfig) {
      return (colConfig.header || colConfig.filter) && colConfig.id !== 'actions'
    },

    isSearchableField (colConfig) {
      return colConfig.header && colConfig.filter !== false && colConfig.id !== 'actions'
    },

    getSearchFields (dataTableConfig) {
      let searchFields = []
      for (const colConfig of Object.values(dataTableConfig.columns)) {
        if (this.isSearchableField(colConfig)) {
          searchFields.push(colConfig.itemText ? colConfig.itemText : colConfig.id)
        }
      }
      return searchFields
    },

    getDTO (dataTableConfig) {
      let dto = {}
      if (dataTableConfig?.columns) {
        for (const colConfig of Object.values(dataTableConfig.columns)) {
          const defaultValue = colConfig.defaultValue

          switch (colConfig.type) {
            case 'number': {
              dto[colConfig.id] = defaultValue !== undefined ? defaultValue : null
              break
            }
            case 'select': {
              dto[colConfig.id] = defaultValue !== undefined ? defaultValue : {}
              break
            }
            case 'tag': {
              dto[colConfig.id] = ''
              break
            }
            default: {
              dto[colConfig.id] = defaultValue !== undefined ? defaultValue : ''
              break
            }
          }
        }
      }
      return dto
    },

    decorateItems (dataTableConfig = {}, items = [], themeIsDark = false) {
      const itemDecoration = dataTableConfig.itemDecoration

      const typeTags = Object.values(dataTableConfig?.columns || {})
        .filter(f => f.type === 'tag')
        .map(f => f.id)

      // Exit when config does not require decoration
      if (!itemDecoration && typeTags.length === 0) return items

      return items.map(item => {
        const decorations = {}

        // Resolve each json-logic and decorate the item with the __<variables>
        const logic = itemDecoration?.backgroundColor && itemDecoration.backgroundColor.jsonLogic
        if (logic) {
          decorations['__backgroundColor'] = logic && jsonLogic.apply(logic, item)
          decorations['__backgroundColor'] = themeIsDark ? decorations['__backgroundColor'].replace('lighten', 'darken') : decorations['__backgroundColor']
        }

        // Parse tags string into an array
        if (typeTags) {
          typeTags.reduce((acc, field) => {
            acc[field] = Configuration.datatable.parseTagsString(
              item[field],
              dataTableConfig?.columns?.[field]?.separator
            )
            return acc
          }, decorations)
        }

        return Object.assign(item, decorations)
      })
    },

    // map item DTO according to data table configuration for viewing
    mapItemDTO (dataTableConfig, itemDTO) {
      const itemDTOCopy = L.cloneDeep(itemDTO)
      if (dataTableConfig?.columns) {
        for (const colConfig of Object.values(dataTableConfig.columns)) {
          if (itemDTOCopy[colConfig.id]) {
            itemDTOCopy[colConfig.id] = this.renderColumn(colConfig, itemDTOCopy)
          }
        }
      }
      return itemDTOCopy
    },

    // map item DTO according to data table configuration for editing
    mapItemDTOForEdit (dataTableConfig, itemDTO) {
      if (dataTableConfig?.columns) {
        for (const colConfig of Object.values(dataTableConfig.columns)) {
          switch (colConfig.type) {
            case 'select': {
              // prepare select vaue for use in FormSelect
              if (itemDTO[colConfig.id] === null) {
                itemDTO[colConfig.id] = {}
              }
              break
            }
            case 'tag': {
              const tags = this.parseTagsString(itemDTO[colConfig.id], colConfig.separator)
              itemDTO[colConfig.id] = tags
              break
            }
          }
        }
      }
      return itemDTO
    },

    // map item DTO according to data table configuration for saving
    mapItemDTOForSave (dataTableConfig, itemDTO) {
      if (dataTableConfig?.columns) {
        for (const colConfig of Object.values(dataTableConfig.columns)) {
          if (colConfig.type === 'select') {
            // select values are processed in a special way
            if ((itemDTO[colConfig.id] || itemDTO[colConfig.id] === 0 || itemDTO[colConfig.id] === false) && // value set
              (
                typeof itemDTO[colConfig.id] !== 'object' || // string/number
                (itemDTO[colConfig.id].id || itemDTO[colConfig.id].id === 0)) // object with id (i.e. assignee)
            ) {
              // expect value itemDTO[colConfig.id] from select control url
              // e.g. { "id": 4, "firstName": "Simone", "lastName": "Alloni" }
              // or "some string"
              // or Number
              const transformJson = {}
              Vue.set(transformJson, colConfig.id, colConfig.outValueExpr)
              this.jsonTransform(itemDTO[colConfig.id], transformJson, itemDTO)
            } else {
              // remove select value from DTO to save as null
              delete itemDTO[colConfig.id]
            }
          } else if (colConfig.type === 'tag') {
            itemDTO[colConfig.id] = this.stringifyTag(itemDTO[colConfig.id], colConfig.separator)
          }
          // TODO: queste 2 righe sono un override del valore: il valore è già messo in fase di costruzione dell'itemDTO in getDTO al posto di null/vuoto
          if (colConfig.readonly && colConfig.defaultValue) {
            itemDTO[colConfig.id] = colConfig.defaultValue
          }

          // to be sure to pass null value instead of blank when required
          if (!itemDTO[colConfig.id] && colConfig.defaultValue === null) {
            itemDTO[colConfig.id] = null
          }

          if ( // If technical fields (not visible in form are marked as required but not filled, throw an error)
            colConfig.required &&
            itemDTO[colConfig.id] === null &&
            !dataTableConfig.formRows.find(r => r.cols.find(c => c.indexOf(colConfig.colId) !== -1))) {
            throw new Error(i18n.t('message.formRequiredTechFieldValidation', { field: colConfig.id }))
          }
        }
      }
      return itemDTO
    },

    TAG_SUPPORTED_SEPARATORS: ['#'],

    getTagsAdmittedSet (separator) {
      const tokenAdmittedSet = '[^\\\\' + separator + ']' // any exept \ and the separator
      return tokenAdmittedSet
    },

    /**
     * Parse the server-side rapresentation of the tags into an array of string
     * @param {String} tagsData The server-side rapresentation of the tags Array: concatened string with delimiters for each tag
     * @returns An array of tags: each tag is a string
     */
    parseTagsString (tagsData = '', separator = '#') {
      if (this.TAG_SUPPORTED_SEPARATORS.indexOf(separator) === -1) throw new Error('Invalid tag separator:', separator)

      let tags = tagsData
      if (!Array.isArray(tags)) {
        tags = []
        if (tagsData) {
          // Match tags using delimiters: '%tag1% %tag2 con spazi% %tag3 50\%%'
          const tokenAdmittedSet = this.getTagsAdmittedSet(separator)
          const reg = new RegExp(separator + '(' + tokenAdmittedSet + '+(?:\\\\' + separator + ')*' + tokenAdmittedSet + '*)' + separator, 'g')
          const regEscape = new RegExp('\\\\' + separator, 'g')
          const matches = tagsData.matchAll(reg)
          for (const match of matches) { // for each match...
            const m = match[0]
              .slice(separator.length, -separator.length) // remove delimiters
              .replaceAll(regEscape, separator) // unescape internal %
              .trim() // cleanup not needed spaces
            tags.push(m)
          }
        }
      }
      return tags
    },

    /**
     * Stringify an Array of tags into the server-side rapresentation
     * @param {String[]} tags An array of tags: each tag is a string
     * @returns The server-side rapresentation of the tags Array: concatened string with delimiters for each tag
     */
    stringifyTag (tags = [], separator = '#') {
      if (Array.isArray(tags) && tags.length > 0) {
        if (this.TAG_SUPPORTED_SEPARATORS.indexOf(separator) === -1) throw new Error('Invalid tag separator:', separator)
        const regEscape = new RegExp(separator, 'g')
        const str = tags
          .map(t => t.replaceAll(regEscape, '\\' + separator)) // escape & inside each tag
          .map(t => separator + t + separator) // add tag delimiters
          .join(' ') // join in single string space-separated

        return str
      }
      return ''
    },

    mergeQueryDataTable (inheritFromDataTable = {}, queryDataTable = {}, {
      addQueryFields = true
    } = {}) {
      // list ids of columns to merge: considere as list the fields exposed by the base datatable
      const colsToMap = queryDataTable?.columns || {}

      const columns = Object.keys(colsToMap).reduce((acc, colId) => {
        let srcCol = inheritFromDataTable?.columns?.[colId]
        let queryCol = queryDataTable?.columns?.[colId]

        if (addQueryFields === false && inheritFromDataTable?.columns && !srcCol) {
          return acc
        }

        // Cleanup margiable fields: when types are different propagate only standard fields
        if (srcCol && srcCol.type && srcCol.type !== queryCol.type) {
          const standardFields = [
            'label',
            'filter',
            'header',
            'linkTo'
          ]

          srcCol = standardFields.reduce((acc, f) => {
            if (srcCol.hasOwnProperty(f)) {
              acc[f] = srcCol[f]
            }
            return acc
          }, {})
        }

        // Merge column description
        acc[colId] = Object.assign(
          { id: colId }, // query-only columns will need the ID
          srcCol,
          queryCol // let it override fields
        )
        return acc
      }, {})

      if (inheritFromDataTable?.columns?.['actions']) {
        columns['actions'] = inheritFromDataTable.columns['actions']
      }

      return Object.assign({}, inheritFromDataTable, queryDataTable, {
        columns
      })
    },

    propagateFieldInheritance (item, output = {}, fieldConfig) {
      if (fieldConfig.propagateTo) {
        const targetFields = Array.isArray(fieldConfig.propagateTo) ? fieldConfig.propagateTo : [fieldConfig.propagateTo]
        targetFields.reduce((acc, targetField) => {
          if (typeof targetField === 'string') {
            acc[targetField] = output[fieldConfig.outValue || fieldConfig.id]
          } else if (typeof targetField === 'object') {
            const { field, jsonLogic: logic } = targetField
            if (field && jsonLogic) {
              acc[field] = jsonLogic.apply(logic, item)
            }
          }

          return acc
        }, output)
      }

      return output
    },

    renderColumn (colConfig, item) {
      let result = item[colConfig.id]
      if (colConfig.renderMethod && item) {
        result = this[colConfig.renderMethod](colConfig, item)
      } else {
        switch (colConfig.type) {
          case 'date': {
            // expect value in java LocalDate format, e.g. 2013-05-30
            result = this.renderFieldDate(colConfig, item)
            break
          }
          case 'datetime': {
            // expect value in UTC format with timezone (from java Instant, e.g. 2013-05-30T23:38:23.085Z)
            result = this.renderFieldDateTime(colConfig, item)
            break
          }
          case 'select': {
            if (colConfig.itemTextExpr) { // itemTextExpr: explicit string interpolation defined in configuration
              // expect value from select control url
              // e.g. { "id": 4, "firstName": "Simone", "lastName": "Alloni" }
              const transformJson = {}
              transformJson[colConfig.itemText] = colConfig.itemTextExpr
              this.jsonTransform(item[colConfig.id], transformJson, item)
              result = item[colConfig.itemText]
            } else if (colConfig.options) { // static options in configuration: get text from selected option value
              result = this.renderFieldSelect(colConfig, item)
            }

            break
          }
          case 'markdown': {
            result = result ? markdownToTxt(result) : ''
            break
          }

          case 'number': {
            switch (colConfig.format) {
              case 'number':
                result = result && result.toLocaleString(i18n.locale, {
                  maximumFractionDigits: 2
                })
                break

              case 'percentage':
                result = result && result.toLocaleString(i18n.locale, {
                  maximumFractionDigits: 2
                })
                result = result != null ? (result + '%') : '0%'
                break

              default:
                break // no format applied
            }
            break
          }

          case 'tag': {
            result = this.renderTags(colConfig, item)
            break
          }
        }
      }
      return result
    },

    // example 1
    //
    // sourceJson { "id": 4, "firstName": "Simone", "lastName": "Alloni" }
    // transformJson { "fullName": "$lastName $firstName" }
    // targetJson { "fullName": "Alloni Simone" }
    //
    // example 2
    //
    // sourceJson { "id": 4, "firstName": "Simone", "lastName": "Alloni" }
    // transformJson { "author": {"id": "$id"} }
    // targetJson { "author": {"id": 4} }
    //
    // example 3
    //
    // sourceJson "italy"
    // transformJson "{ workspaceId: "$id" }
    // targetJson { "workspaceId": "italy" }
    jsonTransform (sourceJson, transformJson, targetJson) {
      if (transformJson && typeof transformJson === 'object' && typeof sourceJson === 'object') {
        // add properties to targetJson
        for (let propName in transformJson) {
          if (typeof transformJson[propName] === 'object') {
            // process nested object
            targetJson[propName] = {}
            this.jsonTransform(sourceJson, transformJson[propName], targetJson[propName])
          } else {
            // transformJson is an expression to be evaluated against sourceJson
            let value = ''
            if (sourceJson) {
              value = transformJson[propName]
              if (typeof value === 'string') {
                const placeholders = Array.from(value.matchAll(/\$[A-Za-z0-9\\._]+/g), (m) => m[0])

                placeholders.forEach((word) => {
                  const itemFieldName = word.slice(1)
                  const _v = L.get(sourceJson, itemFieldName) // itemName can be <field> or a path <field>.<field> (ie: manufacturer.title)

                  if (placeholders.length === 1 && placeholders[0] === value) {
                    value = _v // string contains only 1 token: keep the data type (i.e.: number, string)
                  } else {
                    value = value.replace(word, _v)
                  }
                })
              }
            }
            targetJson[propName] = value
          }
        }
      }
    },

    renderTrackerTypeId (colConfig, item) {
      const trackerTypeId = item[colConfig.id]
      return i18n.t(Configuration.getApplication(item.applicationId).trackerTypes[trackerTypeId].name)
    },

    renderState (colConfig, item) {
      const stateId = item[colConfig.id]
      const statesMap = Configuration.state.getTrackerStatesMap(item)
      return item[colConfig.id] && statesMap[stateId] ? i18n.t(statesMap[stateId].label) : stateId
    },

    renderWorkspace (colConfig, item) {
      let workspace = Configuration.auth.findWorkspace(item.applicationId, item.workspaceId)
      return workspace !== null ? i18n.t(workspace.name) : item.workspaceId
    },

    renderNewLines (colConfig, item) {
      const text = item[colConfig.id]
      console.warn('>>> renderNewLines deprecato, rimuovere da', colConfig.id)
      return text
    },

    renderFieldSelect (colConfig, item) {
      const value = item[colConfig.id] // value saved in tracker
      let output = value
      if (colConfig.options) { // options defined in config
        const itemValueKey = colConfig.itemValue || DEFAULT_ITEM_VALUE
        const itemTextKey = colConfig.itemText || DEFAULT_ITEM_TEXT
        const option = colConfig.options.find(o => o[itemValueKey] === value)
        if (option) {
          output = i18n.t(option[itemTextKey])
        }
      }

      return output
    },

    renderFieldDate (colConfig, item) {
      const value = item[colConfig.id] // value saved in tracker

      // expect dates as YYYY-MM-DD
      const dateFormat = i18n.t(colConfig.format || 'format.date')
      return format(value, dateFormat, 'YYYY-MM-DD')
    },

    renderFieldDateTime (colConfig, item) {
      const value = item[colConfig.id] // value saved in tracker

      // expect dates in UTC from server
      const dateFormat = i18n.t(colConfig.format || 'format.dateTime')
      return format(value, dateFormat)
    },

    renderTags (colConfig, item) {
      const value = item[colConfig.id] // value saved in tracker

      return Array.isArray(value) ? value.join(', ') : value
    },

    renderFieldMultiValue (colConfig, item) {
      const itemValue = item[colConfig.id]

      let output = itemValue
      if (typeof itemValue === 'string' && colConfig.options) {
        const itemValueKey = colConfig.itemValue || DEFAULT_ITEM_VALUE
        const itemTextKey = colConfig.itemText || DEFAULT_ITEM_TEXT
        let arrayMultiValue = itemValue ? itemValue.split(',') : []
        arrayMultiValue = arrayMultiValue.map(v => {
          const option = colConfig.options.find(o => o[itemValueKey] === v.trim())
          if (option) {
            return i18n.t(option[itemTextKey])
          }
        })
        output = arrayMultiValue.length > 1 ? arrayMultiValue.join(', ') : arrayMultiValue[0]
      } else if (Array.isArray(itemValue) && colConfig.itemTextExpr) {
        let arrayMultiValue = itemValue.map(value => {
          const transformJson = {}
          transformJson[colConfig.itemText] = colConfig.itemTextExpr
          this.jsonTransform(value, transformJson, value)
          return value[colConfig.itemText]
        })
        output = arrayMultiValue.length > 1 ? arrayMultiValue.join(', ') : arrayMultiValue[0]
      }
      return output
    },

    renderCurrency (colConfig, item) {
      const value = item[colConfig.id]
      if (value === null || value === undefined) return value

      return value.toLocaleString(i18n.locale, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })
    },

    renderAssociationType (colConfig, item) {
      let associations
      if (item.applicationId && item.trackerTypeId) {
        const application = Configuration.getApplication(item.applicationId)
        const trackerTypeId = colConfig.sourceTrackerTypeId || item.trackerTypeId
        const trackerType = application.trackerTypes[trackerTypeId]
        associations = trackerType.associationTypes
      }

      const value = item[colConfig.id] // value saved in tracker
      let output = value

      if (associations) { // options defined in config
        const option = associations.find(o => o['id'] === value)
        if (option) {
          output = i18n.t(option['label'])
        }
      }

      return output
    },

    renderSize (colConfig, item) {
      let output = item[colConfig.id]
      if (output === 0) {
        output = '0 Bytes'
      } else {
        const k = 1024
        const dm = colConfig.decimals < 0 ? 0 : colConfig.decimals
        const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']
        const i = Math.floor(Math.log(output) / Math.log(k))
        output = parseFloat((output / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i]
      }
      return output
    },

    searchColumnArray (dataTableConfig, filterHeaders) {
      let searchColumnConfigArray = []
      for (const colConfig of Object.values(dataTableConfig.columns)) {
        if (filterHeaders?.length) {
          if (filterHeaders.find(header => header.value === colConfig.id) && colConfig.filter && colConfig.id !== 'actions') {
            searchColumnConfigArray.push(colConfig)
          }
        } else if (this.isSearchableField(colConfig)) {
          searchColumnConfigArray.push(colConfig)
        }
      }
      return searchColumnConfigArray
    },

    // Create the search/filter term given the operator and the query
    searchColumnValue (colConfig, searchTxt, operator = 'LIKE') {
      let valueTobeSearched = searchTxt
      if (searchTxt && colConfig.searchMethod) {
        valueTobeSearched = this[colConfig.searchMethod](colConfig, searchTxt, operator)
      } else if (searchTxt && this.DEFAULT_SEARCH_METHODS[colConfig.type]) {
        valueTobeSearched = this[this.DEFAULT_SEARCH_METHODS[colConfig.type]](colConfig, searchTxt, operator)
      }

      // Search term setup:
      // se dobbiamo fare match su + valori (i.e.: Select) => IN + values
      // se dobbiamo fare un match su stringa secco => <operatore> + value
      if (Array.isArray(valueTobeSearched)) {
        return valueTobeSearched.length > 0 ? {
          operator: operator === 'NOT_IN' ? operator : 'IN',
          values: valueTobeSearched
        } : null
      } else {
        return valueTobeSearched !== null ? {
          operator,
          value: valueTobeSearched
        } : null
      }
    },

    // Filter adapter for the searchColumnValue method
    searchFilterValue (colConfig, filter) {
      let filterText

      if (filter.value !== undefined) {
        filterText = filter.value
      } else if (filter.values) {
        filterText = filter.values
      }
      return this.searchColumnValue(colConfig, filterText, filter.operator)
    },

    // Search function for list of options: it's resoling the operator into the list of items matching the expectation
    // => the list should be used into a IN filter/search term
    searchInOptions (searchTxt, operator, options = [], {
      itemValueField = DEFAULT_ITEM_VALUE,
      itemTextField = DEFAULT_ITEM_TEXT
    }) {
      const query = Array.isArray(searchTxt) ? searchTxt.map(s => s.toUpperCase()) : searchTxt.toUpperCase()

      switch (operator) {
        case 'LIKE':
          return options.filter((item) => {
            const stateLabel = i18n.t(item[itemTextField])

            return stateLabel.toUpperCase().includes(query)
          }).map(i => i[itemValueField])

        case 'NOT_LIKE':
          return options.filter((item) => {
            const stateLabel = i18n.t(item[itemTextField])

            return !stateLabel.toUpperCase().includes(query)
          }).map(i => i[itemValueField])

        case 'EQUALS':
          const state = options.find((item) => {
            const stateLabel = i18n.t(item[itemTextField])
            return stateLabel.toUpperCase() === query
          })

          return state ? state[itemValueField] : null

        case 'NOT_EQUALS':
          return options.filter((item) => {
            const stateLabel = i18n.t(item[itemTextField])
            return stateLabel.toUpperCase() !== query
          }).map(i => i[itemValueField])

        case 'IN' :
        case 'NOT_IN':
          return options.filter((item) => {
            const stateLabel = i18n.t(item[itemTextField])
            return query.includes(stateLabel.toUpperCase())
          }).map(i => i[itemValueField])

        default:
          // operatore non riconosciuto
          return ''
      }
    },

    // Costruisce l'array di "value" di options della select che matchano nel "text" il searchText da cercare
    searchSelectFields (colConfig, searchTxt, operator = 'LIKE') {
      if (colConfig && colConfig.options) {
        return this.searchInOptions(searchTxt, operator, colConfig.options, {
          itemTextField: colConfig.itemText,
          itemValueField: colConfig.itemValue
        })
      }

      return searchTxt // not able to resolve the operator
    },

    searchTagsFields (colConfig, searchTxt, operator = 'LIKE') {
      if (searchTxt) {
        switch (operator) {
          case 'IN':
            return searchTxt.map(ss => Configuration.datatable.stringifyTag([ss], colConfig.separator))

          default:
            return Configuration.datatable.stringifyTag([searchTxt], colConfig.separator)
        }
      }
      return searchTxt
    },

    // Search in "state" column
    searchState (colConfig, searchTxt, operator = 'LIKE') {
      // find column state map from tracker context or from colConfig
      const selectedContext = store.getters['contextStore/selectedContext']
      const trackerTypeId = colConfig.trackerTypeId ? colConfig.trackerTypeId : selectedContext.trackerTypeId
      const statesMap = Configuration.state.getStatesMap(selectedContext.applicationId, trackerTypeId)
      if (statesMap) {
        return this.searchInOptions(searchTxt, operator, Object.values(statesMap), {
          itemValueField: 'id',
          itemTextField: 'label'
        })
      }
      return searchTxt // not able to resolve the operator
    },

    // Search in "workspace" column
    searchWorkspace (colConfig, searchTxt, operator = 'LIKE') {
      // find column state map from tracker context or from colConfig
      const selectedContext = store.getters['contextStore/selectedContext']
      const authorizedApplications = store.getters['configurationStore/auth/applications']
      const workspaces = Configuration.getNestedWorkspaces(selectedContext.applicationId, authorizedApplications[selectedContext.applicationId].workspaces, {})
      if (workspaces) {
        return this.searchInOptions(searchTxt, operator, Object.values(workspaces), {
          itemValueField: 'id',
          itemTextField: 'name'
        })
      }
      return searchTxt // not able to resolve the operator
    },

    // Search in "trackerType ID" column
    searchTrackerTypeId (colConfig, searchTxt, operator = 'LIKE') {
      const selectedContext = store.getters['contextStore/selectedContext']
      const trackerTypes = Configuration.auth.getTrackerTypes(selectedContext.applicationId, selectedContext.workspaceId, 'get')
      if (trackerTypes) {
        const trackerTypesArray = Object.entries(trackerTypes).map(([trackerTypeId, trackerType]) => {
          return Object.assign(trackerType, { 'id': trackerTypeId })
        })
        return this.searchInOptions(searchTxt, operator, trackerTypesArray, {
          itemValueField: 'id',
          itemTextField: 'name'
        })
      }
      return searchTxt // not able to resolve the operator
    },

    // Identify in columns config ALL the fileds that are filterable. For nested fields, split into multiple fields
    searchFilterableFields (columns) {
      const t = columns
        .filter(c => c.filter)
        .reduce((acc, colConfig) => {
          let filterType
          switch (colConfig.type) {
            case 'number':
            case 'date':
            case 'datetime':
            case 'time':
            case 'tag':
              filterType = colConfig.type
              break

            default:
              filterType = 'text'
          }

          if (typeof colConfig.outValueExpr === 'object' && colConfig.itemTextExpr) {
            // use itemTextExpr to identify nested search fields: split filters selection in N nested fields
            const wordsArray = colConfig.itemTextExpr.split(' ')
            wordsArray.forEach((word) => {
              if (word[0] === '$') {
                const itemFieldName = word.slice(1)
                const nestedFieldName = `${colConfig.id}.${itemFieldName}`
                const capitalizedFieldName = itemFieldName.charAt(0).toUpperCase() + itemFieldName.slice(1)

                acc.push({
                  id: nestedFieldName,
                  name: i18n.t(`${colConfig.label}${capitalizedFieldName}`), // i.e. sales.label.ownerLastName
                  type: filterType
                })
              }
            })
          } else {
            // Regualr fields
            acc.push({
              id: colConfig.id,
              name: i18n.t(colConfig.label),
              type: filterType
            })
          }

          return acc
        }, [])

      return t
    },

    /*
    request body includes pagination and search filters

        String inputFirstPage = "{" +
                "  \"page\": 0,"+
                "  \"size\": 5,"+
                "  \"sortTerms\": [" +
                "    {" +
                "      \"sortField\": \"id\"," +
                "      \"sortType\": \"ASC\"," +
                "    }" +
                "  ]" +
                "  \"terms\": [" +
                "    {" +
                "      \"field\": \"title\"," +
                "      \"operator\": \"LIKE\"," +
                "      \"value\": \"some title\"" +
                "    }," +
                "    { " +
                "      \"field\": \"state\"," +
                "      \"operator\": \"LIKE\"," +
                "      \"value\": \"backlog\"" +
                "    }" +
                "  ]" +
                "}";
    */
    paginatedRequestBody (dataTableConfig, options = {}, _searchTxt = '', filterArray = [], filterHeaders) {
      // paging parameters
      const body = options.itemsPerPage < 0 ? {} : { page: options.page > 0 ? options.page - 1 : 0, size: options.itemsPerPage }
      // sort parameters
      if (options.sortBy?.length && options.sortDesc?.length && options.sortBy.length === options.sortDesc.length) {
        body.sortTerms = []
        options.sortBy.forEach((field, index) => {
          body.sortTerms.push({ sortField: field, sortType: options.sortDesc[index] === true ? 'DESC' : 'ASC' })
        })
      }
      // search parameters
      if (_searchTxt && this.searchColumnArray(dataTableConfig, filterHeaders)) {
        const searchTxt = _searchTxt.trim()
        body.terms = []
        for (let colConfig of this.searchColumnArray(dataTableConfig, filterHeaders)) {
          switch (colConfig.type) {
            case 'select': {
              if (typeof colConfig.outValueExpr === 'object' && colConfig.itemTextExpr) {
                // use itemTextExpr to identify nested search fields
                const wordsArray = colConfig.itemTextExpr.split(' ')
                wordsArray.forEach((word) => {
                  if (word[0] === '$') {
                    const itemFieldName = word.slice(1)
                    const nestedFieldName = `${colConfig.id}.${itemFieldName}`

                    const resolvedTerm = this.searchColumnValue(colConfig, searchTxt)
                    resolvedTerm && body.terms.push({
                      field: nestedFieldName,
                      ...resolvedTerm
                    })
                  }
                })
              } else {
                // handle as standard search field
                const resolvedTerm = this.searchColumnValue(colConfig, searchTxt)
                resolvedTerm && body.terms.push({
                  field: colConfig.id,
                  ...resolvedTerm
                })
              }
              break
            }
            default: {
              const resolvedTerm = this.searchColumnValue(colConfig, searchTxt)
              resolvedTerm && body.terms.push({
                field: colConfig.id,
                ...resolvedTerm
              })
              break
            }
          }
        }
      }
      // filtering parameters
      body.filterTerms = filterArray.reduce((acc, filter) => {
        const colId = filter.field.split('.')[0] // support also filters on nested fields (i.e.: assignee.lastName)
        const colConfig = Object.values(dataTableConfig.columns).find(c => c.id === colId)

        if (colConfig) {
          const resolvedTerm = this.searchFilterValue(colConfig, filter)
          if (resolvedTerm) {
            acc.push({
              field: filter.field,
              ...resolvedTerm
            })
          } else {
            // filter term not resolved (i.e.: match by translation did not provide any value/values)
            store.dispatch('notificationStore/notifyWarning', {
              message: i18n.t('label.filtersSearchWarning', { field: filter.field })
            })
          }
        }

        return acc
      }, [])

      return body
    },
    paginatedRequestBodyTerms (searchTxt, columnArray) {
      return columnArray.filter(column => { return column !== undefined }).map(column => {
        return {
          field: column,
          operator: 'LIKE',
          value: searchTxt
        }
      })
    },
    validateSearchFilter (filter, dataTableColumns = {}) {
      const colId = filter.field.split('.')[0] // support also filters on nested fields (i.e.: assignee.lastName)
      const colConfig = Object.values(dataTableColumns).find(c => c.id === colId)

      if (colConfig) {
        const resolvedTerm = this.searchFilterValue(colConfig, filter)
        return !!resolvedTerm
      }

      return false
    },

    // Validate a form (required fields)
    validateForm (columns = {}, itemDTO = {}) {
      return Object.values(columns).reduce((acc, colConfig) => {
        if (colConfig.required) {
          return acc && !!(itemDTO[colConfig.id] || itemDTO[colConfig.id] === 0) &&
          // validation for select
          (typeof itemDTO[colConfig.id] !== 'object' || !L.isNil(itemDTO[colConfig.id].id))
        }
        return acc
      }, true)
    },

    getRequiredLabel (colConfig) {
      const label = colConfig.labelEdit ? i18n.t(colConfig.labelEdit) : i18n.t(colConfig.label)
      return colConfig.required ? `${label} *` : label
    }
  },

  i18n: {
    overrideLabel (applicationId, label) {
      const overrideLabel = `${applicationId}.${label}`
      return this.labelExists(overrideLabel) ? overrideLabel : label
    },

    labelExists (label) {
      return i18n.t(label) !== label
    },

    capitalizePhrase (phrase) {
      return phrase.charAt(0).toUpperCase() + phrase.slice(1).toLowerCase()
    }
  },

  /* the simple table configuration api */

  simpleTable: {

    getChildrenTable (applicationId, trackerTypeId) {
      const trackerType = Configuration.getApplication(applicationId)?.trackerTypes?.[trackerTypeId]
      return trackerType && trackerType.childrenTable
        ? trackerType.childrenTable
        : Configuration.getApplication(applicationId).childrenTable
    },

    getAssociationsTable (applicationId, trackerTypeId) {
      const trackerType = Configuration.getApplication(applicationId)?.trackerTypes?.[trackerTypeId]
      return trackerType && trackerType.associationTable
        ? trackerType.associationTable
        : Configuration.getApplication(applicationId).associationTable
    }
  },

  /* the state machine configuration api */

  state: {

    getStateMachine (applicationId, trackerTypeId) {
      let stateMachine
      if (applicationId && trackerTypeId) {
        const application = Configuration.getApplication(applicationId)
        const trackerType = application.trackerTypes[trackerTypeId]
        stateMachine = application.stateMachines[trackerType.stateMachine]
      }
      return stateMachine
    },

    getStatesMap (applicationId, trackerTypeId) {
      let map = {}
      const stateMachine = this.getStateMachine(applicationId, trackerTypeId)
      if (stateMachine) {
        map = stateMachine.states.reduce((accumulator, stateObj) => {
          accumulator[stateObj.id] = stateObj
          return accumulator
        }, map)
      }
      return map
    },

    getTrackerStateMachine (trackerDTO) {
      return this.getStateMachine(trackerDTO.applicationId, trackerDTO.trackerTypeId)
    },

    getTrackerStatesMap (trackerDTO) {
      return this.getStatesMap(trackerDTO.applicationId, trackerDTO.trackerTypeId)
    },

    getTrackerEventsMap (trackerDTO) {
      let map = {}
      const stateMachine = this.getTrackerStateMachine(trackerDTO)
      if (stateMachine) {
        map = stateMachine.events.reduce((accumulator, eventObj) => {
          accumulator[eventObj.id] = eventObj
          return accumulator
        }, map)
      }
      return map
    },

    // possible events / actions for the current state of a tracker
    // e.g. { id: 'gotoInProgress', label: 'Go to in progress' }
    getEventsFromState (trackerDTO) {
      const stateMachine = this.getTrackerStateMachine(trackerDTO)
      const state = trackerDTO.state

      let eventsFromState = trackerDTO.eventsFromState ? trackerDTO.eventsFromState : []
      if (stateMachine && eventsFromState.length > 0) {
        // map server side trackerDTO events to events from stateMachine configuration
        eventsFromState = eventsFromState.map(eventId => {
          const eventConfig = stateMachine.events.find(eventObj => { return eventObj.id === eventId })
          if (eventConfig) {
            const transition = stateMachine?.transitions?.find(t => t.from === state && t.event === eventId)

            return {
              id: eventId,
              label: i18n.t(eventConfig.label),
              validationLogic: eventConfig?.validationLogic,
              targetState: transition && transition.to
            }
          }
          console.error('getEventsFromState: received invalid state', eventId)
          return null
        }).filter(Boolean)
      }

      return eventsFromState
    }
  },

  /* the authorization configuration api */

  auth: {
    /**
     * Replace the placeholders of a privilegeId
     *
     * @param {String} privilegeId The privilegeId to resolve
     * @param {Object} context The privilege context: workspaceId
     * @returns The privilegeId with the placeholder resolved
     */
    resolvePlaceholders (privilegeId, { workspaceId } = {}) {
      return privilegeId.replace('$workspace', workspaceId)
    },

    /**
     * It verifies if given privilegeId is excluded by all the given roles.
     * The algorithm will return:
     * - true when the privilage is excluded by all the given roles
     * - false when the privilage is not excluded by all the given roles
     *
     * @param {String} privilegeId The privilegeId to analyze
     * @param {Array} roles The roles to compare with
     * @param {String} workspaceId The workspaceId context
     * @returns {boolean} true when the privilage is excluded by all the given Roles
     */
    privilegeExcludedByAllRoles (privilegeId, roles, workspaceId) {
      return roles.reduce((acc, role) => {
        const excluded = this.privilegeExcludedByRole(
          privilegeId,
          role,
          { workspaceId: workspaceId }
        )
        return acc && excluded
      }, true)
    },

    /**
     * It verifies if the given privilegeId is excluded by the given role.
     * The algorithm will return:
     * - true when the privilege is excluded by the given role
     * - false when the privilege is not excluded by the given role
     *
     * @param {String} privilegeId The privilegeId to analyze
     * @param {Object} role The role to compare with
     * @param {Object} workspaceId The privilage context: workspaceId
     * @returns {boolean} true when the privilege is excluded by the given Role
     */
    // Search Rules:
    // - (A) if <role> does not have exclude:
    //    + exclude is undefined: <role> is not considered, return true
    //    + exclude is an empty array: <role> is considered and the following rules still apply
    // - (B) check the include to find if <role> can access workspace and tracker related to <privilegeId>,
    //   if it can't then <privilegeId> is considered excluded by <role>, return true
    // - (C) <privilegeId> is searched within the exclude of <role>
    // - (D) the workplace placeholder within <privilegeId> is replaced with the <workspaceId> passed,
    //   the resulted computed privilegeId is searched within the exclude of <role>
    privilegeExcludedByRole (privilegeId, role, { workspaceId } = {}) {
      const roleExclude = role?.exclude
      const roleInclude = role?.include

      // (B): Get Include-permission at workspace & tracker level related to <privilegeId>
      const privilegeIdEscaped = privilegeId.split('?')[0]
      const privilageTrackerInclude = privilegeIdEscaped.split('/').slice(0, 6).join('/')
      const computedPrivilageTrackerInclude = this.resolvePlaceholders(privilageTrackerInclude, { workspaceId })
      const privilageWorkspaceInclude = privilegeIdEscaped.split('/').slice(0, 4).join('/')
      const computedPrivilageWorkspaceInclude = this.resolvePlaceholders(privilageWorkspaceInclude, { workspaceId })

      // (D): Resolve placeholders in <privilageId>
      const computedPrivilegeId = this.resolvePlaceholders(privilegeId, { workspaceId })

      if (!roleExclude) { // (A) if <role> exclude is undefined then return true
        return true
      } else if (roleInclude && // (B) if <role> can access workspace/tracker related to <privilegeId> then check (C) and (D)
        (
          roleInclude.indexOf(privilageTrackerInclude) >= 0 ||
          roleInclude.indexOf(computedPrivilageTrackerInclude) >= 0 ||
          roleInclude.indexOf(privilageWorkspaceInclude) >= 0 ||
          roleInclude.indexOf(computedPrivilageWorkspaceInclude) >= 0
        )
      ) {
        return (
          roleExclude.indexOf(privilegeId) >= 0 || // (C) explicit privilege
          roleExclude.indexOf(computedPrivilegeId) >= 0 // (D) privilege with resolved placeholders
        )
      } else {
        return true // (B) <role> can't access workspace/tracker related to <privilegeId>
      }
    },

    /**
     * Merge roles definend in configurations
     * @param {*} roles Roles from application configuration
     * @returns The roles object with the include/exclude array merged
     */
    mergeRoles (roles = {}) {
      return Object.entries(roles).reduce((acc, [roleId, role]) => {
        if (role.extends) {
          const extendedRole = roles[role.extends]
          const newRole = {
            include: L.uniq([
              ...(extendedRole?.include || []),
              ...(role.include || [])
            ])
          }
          // Manage exclude only when of the 2 roles defienes exclusions
          if (extendedRole?.exclude || role.exclude) {
            newRole.exclude = L.uniq([
              ...(extendedRole?.exclude || []),
              ...(role.exclude || [])
            ])
          }
          return Object.assign(acc, { [roleId]: newRole })
        }
        return Object.assign(acc, { [roleId]: role })
      }, {})
    },

    addUserPrivilegesFromWorkspaces (userId, appId, appRoles, appWorkspaces, userPrivilegeSet, userPrivilegeExclusionSet) {
      if (appWorkspaces) {
        for (const workspaceName in appWorkspaces) {
          const workspace = appWorkspaces[workspaceName]
          const userRoles = this.userWorkspaceRoles(userId, appId, workspace)
          if (userRoles && userRoles.length > 0) {
            userRoles.forEach((roleId, index, allRoles) => {
              const currentRole = appRoles[roleId]
              if (currentRole && currentRole.include) {
                currentRole.include.forEach((privilegeId) => {
                  this.userPrivilegeSetAdd(workspace.id, privilegeId, userPrivilegeSet)
                })
              }

              if (currentRole && currentRole.exclude) {
                const otherRoles = allRoles.reduce((acc, otherRoleId) => {
                  if (otherRoleId !== roleId) {
                    acc.push(appRoles[otherRoleId])
                  }
                  return acc
                }, [])

                // Filter exclusions on the list of roles: keep only exclusions listed on all roles defining exclusions
                const exclusions = currentRole.exclude.filter((privilegeId) => {
                  return this.privilegeExcludedByAllRoles(
                    privilegeId,
                    otherRoles,
                    workspace.id
                  )
                })

                exclusions.forEach((privilegeId) => {
                  this.userPrivilegeSetAdd(workspace.id, privilegeId, userPrivilegeExclusionSet)
                })
              }
            })
          }
          this.addUserPrivilegesFromWorkspaces(
            userId, appId, appRoles, appWorkspaces[workspaceName].workspaces, userPrivilegeSet, userPrivilegeExclusionSet)
        }
      }
    },

    userPrivilegeSetAdd (workspaceId, privilegeId, privilegeSet) {
      if (!privilegeId.includes(`/workspace/`) ||
          privilegeId.includes(`/$workspace`) ||
          privilegeId.endsWith(`/workspace/${workspaceId}`) ||
          privilegeId.includes(`/${workspaceId}/`)) {
        const computedPrivilegeId = this.resolvePlaceholders(privilegeId, { workspaceId })
        privilegeSet.add(computedPrivilegeId)
      }
    },

    userHasPrivilege (privilegeId) {
      return store.state.userDataStore.privileges.indexOf(privilegeId) !== -1
    },

    userHasPrivilegeExclusion (privilegeId) {
      return store.state.userDataStore.privilegeExclusions.indexOf(privilegeId) !== -1
    },

    userHasDbacFilterPrivilege (selectedContext, colId) {
      let privId = `/${selectedContext.applicationId}`

      // Add workspace only if defined
      if (selectedContext.workspaceId) {
        privId += `/workspace/${selectedContext.workspaceId}`
      }

      privId += `/tracker/${selectedContext.trackerTypeId}/dbac?${colId}=`

      return store.state.userDataStore.privileges.find(privilege => privilege.startsWith(privId))
    },

    userCanDoAndAccessFeature (selectedContext, featureId, operation, item, userId) {
      switch (operation) {
        case 'get':
          return this.userCanDo(selectedContext, operation, item, userId) &&
            this.userCanAccessFeature(selectedContext, featureId, operation, item, userId)
        case 'post':
        case 'put':
        case 'delete':
          return (this.userCanDo(selectedContext, 'post', item, userId) || this.userCanDo(selectedContext, 'put', item, userId)) &&
            this.userCanAccessFeature(selectedContext, featureId, operation, item, userId)
        default: return false
      }
    },

    // userCanDo checks included / owned privileges

    userCanDo (selectedContext, operation, item, userId) {
      let privId = `/${selectedContext.applicationId}`
      if (selectedContext.workspaceId) {
        privId = privId.concat(`/workspace/${selectedContext.workspaceId}`)
      }
      privId = privId.concat(`/tracker/${selectedContext.trackerTypeId}?op=${operation}`)
      let privAllId = `/${selectedContext.applicationId}`
      if (selectedContext.workspaceId) {
        privAllId = privAllId.concat(`/workspace/${selectedContext.workspaceId}`)
      }
      privAllId = privAllId.concat('/*')
      let canDo = this.userHasPrivilege(privId) || this.userHasPrivilege(privAllId)
      if (!canDo && item && userId && item.createdBy === userId) {
        canDo = this.userHasPrivilege(privId.concat('&on=owned'))
      }
      return canDo
    },
    // userCanAccessField checks excluded / not owned privileges
    /**
     * The `userCanAccessField` method checks column privileges based on:
     * - **selectedContext**: contains applicationId, workspaceId (optional), and trackerTypeId
     * - **colId**: the field identifier
     * - **featureId**: optional feature context (e.g., 'workpackage', 'workpackage3')
     * - **operation**: optional operation type (get, put, create, delete, edit)
     * @param selectedContext
     * @param colId
     * @param featureId
     * @param operation
     * @returns {boolean}
     */
    userCanAccessField (selectedContext, colId, featureId, operation) {
      // Build base privilege path
      let privAllId = `/${selectedContext.applicationId}`

      // Add workspace if present
      if (selectedContext.workspaceId) {
        privAllId = privAllId.concat(`/workspace/${selectedContext.workspaceId}`)
      }

      // Add tracker
      privAllId = privAllId.concat(`/tracker/${selectedContext.trackerTypeId}`)

      // Add feature if present
      if (featureId) {
        privAllId = privAllId.concat(`/${featureId}`)
      }

      // Add colId
      privAllId = privAllId.concat(`?colId=${colId}`)

      // Check base privilege exclusion
      if (this.userHasPrivilegeExclusion(privAllId)) {
        return false
      }

      // If operation is provided, check operation-specific privilege
      if (operation) {
        const privId = privAllId.concat(`&op=${operation}`)
        if (this.userHasPrivilegeExclusion(privId)) {
          return false
        }
      }

      return true
    },

    // userCanAccessAction checks excluded / owned privileges

    userCanAccessAction (selectedContext, actionId) {
      let privId = `/${selectedContext.applicationId}`
      if (selectedContext.workspaceId) {
        privId += `/workspace/${selectedContext.workspaceId}`
      }
      privId += `/tracker/${selectedContext.trackerTypeId}/action?op=${actionId}`

      return !this.userHasPrivilegeExclusion(privId) &&
             !this.userHasPrivilegeExclusion(privId.concat(`&on=owned`))
    },

    // userCanAccessFeature checks excluded / not owned privileges

    userCanAccessFeature (selectedContext, featureId, operation, item, userId) {
      let privId = `/${selectedContext.applicationId}`

      // Add workspace only if defined
      if (selectedContext.workspaceId) {
        privId += `/workspace/${selectedContext.workspaceId}`
      }

      privId += `/tracker/${selectedContext.trackerTypeId}/${featureId}`

      const state = !!(item && item.state)
      const notOwned = userId && item && item.createdBy !== userId

      const strOperation = `op=${operation}`
      const strNotOwned = 'on=notowned'
      const strState = `state=${item && item.state}`

      const excludes = [privId] // full feature exclusion
      if (operation) { excludes.push(privId.concat(`?${strOperation}`)) } // exclude operation
      if (state) { excludes.push(privId.concat(`?${strState}`)) } // exclude state
      if (notOwned) { excludes.push(privId.concat(`?${strNotOwned}`)) } // exclude notOwned
      if (operation && state) { excludes.push(privId.concat(`?${strOperation}&${strState}`)) } // exclude operation + state
      if (operation && notOwned) { excludes.push(privId.concat(`?${strOperation}&${strNotOwned}`)) } // exclude operation + notOwner
      if (state && notOwned) { excludes.push(privId.concat(`?${strState}&${strNotOwned}`)) } // exclude state + notOwned
      if (operation && state && notOwned) { excludes.push(privId.concat(`?${strOperation}&${strState}&${strNotOwned}`)) } // exclude operation + state + notOwned

      return !excludes.some(this.userHasPrivilegeExclusion)
    },

    // userCanAccessFeature checks included privileges

    userCanAccessFeatureInclude (selectedContext, featureId, operation, item, userId) {
      let privId = `/${selectedContext.applicationId}`

      // Add workspace only if defined
      if (selectedContext.workspaceId) {
        privId += `/workspace/${selectedContext.workspaceId}`
      }

      privId += `/tracker/${selectedContext.trackerTypeId}/${featureId}`

      const state = !!(item && item.state)
      const notOwned = userId && item && item.createdBy !== userId

      const strOperation = `op=${operation}`
      const strNotOwned = 'on=notowned'
      const strState = `state=${item && item.state}`

      const includes = [privId] // full feature included
      if (operation) { includes.push(privId.concat(`?${strOperation}`)) } // include operation
      if (state) { includes.push(privId.concat(`?${strState}`)) } // include state
      if (notOwned) { includes.push(privId.concat(`?${strNotOwned}`)) } // include notOwned
      if (operation && state) { includes.push(privId.concat(`?${strOperation}&${strState}`)) } // include operation + state
      if (operation && notOwned) { includes.push(privId.concat(`?${strOperation}&${strNotOwned}`)) } // include operation + notOwner
      if (state && notOwned) { includes.push(privId.concat(`?${strState}&${strNotOwned}`)) } // include state + notOwned
      if (operation && state && notOwned) { includes.push(privId.concat(`?${strOperation}&${strState}&${strNotOwned}`)) } // include operation + state + notOwned

      return includes.some(this.userHasPrivilege)
    },

    userCanAccessFeatureAssociation (selectedContext, associationType, operation, item, userId) {
      const featureId = `association/${associationType}`
      return this.userCanAccessFeature(selectedContext, featureId, operation, item, userId)
    },

    userCanAccessTargetTracker (context, associationDTO, operation, trackerDTO, userId) {
      let reverseAssociationContext = {
        applicationId: associationDTO.target.applicationId,
        workspaceId: associationDTO.target.workspaceId,
        trackerTypeId: associationDTO.target.trackerTypeId
      }
      let canAccessSourceAssociation = this.userCanAccessFeatureAssociation(context, associationDTO.type, operation, trackerDTO, userId)
      let canAccessReverseAssociation = this.userCanDo(reverseAssociationContext, operation, associationDTO.target, userId)

      return canAccessSourceAssociation && canAccessReverseAssociation
    },

    userCanAccessFeatureDataTable (selectedContext, featureId, accessType = 'tracker') {
      let privId = `/${selectedContext.applicationId}`
      if (selectedContext.workspaceId) {
        privId += `/workspace/${selectedContext.workspaceId}`
      }
      privId += `/${accessType}/${selectedContext.trackerTypeId}/datatable/${featureId}`
      return this.userHasPrivilege(privId)
    },

    userCanDoDocumentOperation (selectedContext, operation) {
      let privId = `/${selectedContext.applicationId}`
      if (selectedContext.workspaceId) {
        privId = privId.concat(`/workspace/${selectedContext.workspaceId}`)
      }
      privId = privId.concat(`/document/${selectedContext.trackerTypeId}?op=${operation}`)

      let privAllId = `/${selectedContext.applicationId}`
      if (selectedContext.workspaceId) {
        privAllId = privAllId.concat(`/workspace/${selectedContext.workspaceId}/*`)
      } else {
        privAllId = privAllId.concat(`/document/*`)
      }

      return this.userHasPrivilege(privId) || this.userHasPrivilege(privAllId)
    },

    userCanAccessSettings (applicationId) {
      const privId = `/${applicationId}/settings`
      const privAllId = `/${applicationId}/settings/*`

      return (this.userHasPrivilege(privId) || this.userHasPrivilege(privAllId)) &&
        (Configuration.settings.getAvailableApplicationSettingsId(applicationId).length > 0)
    },

    userCanAccessProfileSetting (profileSettingId) {
      const privId = `/agilis/userProfile/${profileSettingId}`
      return (this.userHasPrivilege(privId))
    },

    userWorkspaceRoles (userId, applicationId, workspace) {
      // search user in workspace tree starting from root
      // so that user roles apply to the workspace child workspaces
      const roles = []
      let workspaceId
      const application = Configuration.getApplication(applicationId)
      const appWorkspaces = application.workspaces

      const appUsers = application.users
      if (appUsers && appUsers[userId]) {
        roles.push(...appUsers[userId].roles)
      }

      const workspaceNameArray = workspace.id.split('.')
      let workspaceRoles = []
      let rolesFromUser = []
      let rolesFromGroups = []

      workspaceNameArray.every((workspaceName) => {
        workspaceId = workspaceId ? `${workspaceId}.${workspaceName}` : workspaceName
        const workspacePath = workspaceId.replace(/\./g, '.workspaces.')
        let workspace = L.get(appWorkspaces, workspacePath, null)

        if (workspace.users && workspace.users[userId]) {
          // user in workspace users list
          rolesFromUser = workspace.users[userId].roles
        } else if (workspace.groups) {
          // check user groups
          for (const groupId in workspace.groups) {
            const group = workspace.groups[groupId]
            const userNameRegex = new RegExp(group.regex)
            if (userNameRegex.test(userId)) {
              // user in workspace group
              rolesFromGroups = group.roles
            }
          }
        }
        return true
      })

      if (rolesFromUser.length > 0) {
        workspaceRoles = rolesFromUser
      } else if (rolesFromGroups.length > 0) {
        workspaceRoles = rolesFromGroups
      }
      roles.push(...workspaceRoles)

      return L.uniq(roles)
    },

    getNestedWorkspaces (applicationId, inWorkspaces, outWorkspaces) {
      // return all workspaces nested inside inWorkspaces
      for (const workspaceName in inWorkspaces) {
        const workspaceId = inWorkspaces[workspaceName].id
        if (this.userHasPrivilege(`/${applicationId}/workspace/${workspaceId}`)) {
          outWorkspaces[workspaceId] = inWorkspaces[workspaceName]
        }
        // traverse nested workspaces
        if (inWorkspaces[workspaceName].workspaces) {
          this.getNestedWorkspaces(
            applicationId, inWorkspaces[workspaceName].workspaces, outWorkspaces)
        }
      }
      return outWorkspaces
    },

    findWorkspace (applicationId, workspaceId, checkPrivilege = true) {
      const application = Configuration.getApplication(applicationId)
      try {
        const appWorkspaces = application.workspaces
        const workspacePath = workspaceId.replace(/\./g, '.workspaces.')
        let workspace = L.get(appWorkspaces, workspacePath, null)
        if (checkPrivilege && !this.userHasPrivilege(`/${applicationId}/workspace/${workspaceId}`)) {
          workspace = null
        }
        return workspace
      } catch (e) {
        return null
      }
    },

    getTrackerTypes (applicationId, workspaceId, operation) {
      const authTrackerTypes = {}
      const selectedAppId = applicationId
      const appConfig = Configuration.getApplication(selectedAppId)
      const configTrackerTypes = appConfig.trackerTypes
      const workspace = this.findWorkspace(selectedAppId, workspaceId)
      if (workspace) {
        for (const trackerTypeId in configTrackerTypes) {
          if (workspace.trackers.indexOf(trackerTypeId) !== -1 &&
            (this.userHasPrivilege(`/${selectedAppId}/workspace/${workspaceId}/tracker/${trackerTypeId}`) ||
             this.userHasPrivilege(`/${selectedAppId}/workspace/${workspaceId}/*`))) {
            if (operation) {
              if (this.userHasPrivilege(`/${selectedAppId}/workspace/${workspaceId}/tracker/${trackerTypeId}?op=${operation}`)) {
                authTrackerTypes[trackerTypeId] = configTrackerTypes[trackerTypeId]
              }
            } else {
              authTrackerTypes[trackerTypeId] = configTrackerTypes[trackerTypeId]
            }
          }
        }
      } else {
        Vue.prototype.$debug(`getTrackerTypes workspace not found [${workspaceId}]`)
      }
      return authTrackerTypes
    },

    getTrackerTypeIds (applicationId, workspaceId, operation) {
      return Object.keys(this.getTrackerTypes(applicationId, workspaceId, operation))
    },

    getReportIds (applicationId, workspaceId) {
      if (applicationId) {
        const app = Configuration.getApplications()[applicationId]
        const reports = app?.reports || {}
        return Object.keys(reports).filter(reportId => {
          const privilegeId = `/${applicationId}/workspace/${workspaceId}/report/${reportId}`
          if (reportId && reports[reportId] && Configuration.auth.userHasPrivilege(privilegeId)) {
            return reportId
          }
        })
      }
      return []
    },

    getDashboardIds (applicationId, workspaceId) {
      // should initialize empty array to be returned if no dashboards exist
      const { dashboard } = Configuration.getApplication(applicationId)
      if (dashboard) {
        const dashboardIds = Object.keys(dashboard)
        return dashboardIds.filter(dashboardId => {
          const privilegeId = `/${applicationId}/workspace/${workspaceId}/dashboard/${dashboardId}`
          if (dashboardId && dashboard[dashboardId] && Configuration.auth.userHasPrivilege(privilegeId)) {
            // dashboardId canno be empty because it is an object key
            // same as dashboard[dashboardId]
            // only useful check is this Configuration.auth.userHasPrivilege(permit)
            return true
          }
        })
      }
    },

    getDefaultTrackerTypeId (applicationId, workspaceId) {
      const trackerTypesId = this.getTrackerTypeIds(applicationId, workspaceId)
      if (trackerTypesId.length > 0) {
        return trackerTypesId[0]
      }
      return null
    },

    getDefaultReportId (applicationId, workspaceId) {
      const reportIds = this.getReportIds(applicationId, workspaceId)
      if (reportIds.length > 0) {
        return reportIds[0]
      }
      return null
    },

    getDefaultDashboardId (applicationId, workspaceId) {
      const dashboardIds = this.getDashboardIds(applicationId, workspaceId)
      if (dashboardIds.length > 0) {
        return dashboardIds[0]
      }
      return null
    },

    getHeaders (dataTableConfig, selectedContext, featureId) {
      let headers = Configuration.datatable.getHeaders(dataTableConfig)
      headers = headers.filter((header) => {
        // header value contains column id
        return this.userCanAccessField(selectedContext, header.value, featureId)
      })
      return headers
    },

    getAvailableHeaders (dataTableConfig, selectedContext) {
      let headers = Configuration.datatable.getAvailableHeaders(dataTableConfig)
      headers = headers.filter((header) => {
        // header value contains column id
        return this.userCanAccessField(selectedContext, header.value)
      })
      return headers
    },

    searchFilterableFields (columns, selectedContext) {
      let fields = Configuration.datatable.searchFilterableFields(columns)
      fields = fields.filter((field) => {
        return this.userCanAccessField(selectedContext, field.id.split('.')[0])
      })
      return fields
    },

    getEventsFromState (trackerDTO) {
      let eventsFromState = Configuration.state.getEventsFromState(trackerDTO)
      eventsFromState = eventsFromState.filter((eventObj) => {
        // trackerDTO contains selectedContext, eventId is same as actionId
        return this.userCanAccessAction(trackerDTO, eventObj.id)
      })
      return eventsFromState
    },

    getDTO (dataTableConfig, selectedContext) {
      let dto = Configuration.datatable.getDTO(dataTableConfig)
      for (const colId of Object.getOwnPropertyNames(dto)) {
        if (!this.userCanAccessField(selectedContext, colId)) {
          delete dto[colId]
        }
      }
      return dto
    }
  },

  /* the tour configuration api */

  tour: {

    getTourList (inTourConfig, outTourList = {}) {
      for (const tourNamespace in inTourConfig) {
        const tourNamespaceId = inTourConfig[tourNamespace].id
        if (inTourConfig[tourNamespace].vuetour) {
          const vuetours = inTourConfig[tourNamespace].vuetour
          for (const tour in vuetours) {
            if (vuetours[tour].steps) {
              outTourList[tourNamespaceId + '.' + tour] = vuetours[tour]
            } else {
              const modifierTours = vuetours[tour]
              for (const modifier in modifierTours) {
                if (modifierTours[modifier].steps) {
                  outTourList[tourNamespaceId + '.' + tour + '.' + modifier] = modifierTours[modifier]
                }
              }
            }
          }
        }
        if (inTourConfig[tourNamespace].tour) {
          this.getTourList(inTourConfig[tourNamespace].tour, outTourList)
        }
      }
      return outTourList
    }

  },

  /* the query configuration api */

  query: {
    getQuery (applicationId, queryId) {
      let query
      const application = Configuration.getApplication(applicationId)
      if (application) {
        query = L.get(application, 'query.' + queryId, null)
      }
      return query
    },

    getQueryDatatable (applicationId, queryId) {
      let datatable
      const application = Configuration.getApplication(applicationId)
      if (application) {
        datatable = L.get(application, 'query.' + queryId + '.dataTable', null)
      }
      return datatable
    },

    getQueryDatatableHeaders (dataTableConfig, context) {
      let dataTableHeaders = []
      if (dataTableConfig?.columns) {
        for (const colConfig of Object.values(dataTableConfig.columns)) {
          if (colConfig.header !== false) {
            let header = {}
            header.value = colConfig.id
            header.text = colConfig.label ? i18n.t(colConfig.label) : colConfig.id
            header.align = (colConfig.align ? colConfig.align : (colConfig.type === 'number' ? 'end' : undefined))
            dataTableHeaders.push(header)
          }
        }
      }
      return Configuration.datatable.sortDataTableHeaders(dataTableHeaders, dataTableConfig.columnsOrder)
    }

  },

  document: {
    getAvailableDocuments: (applicationId, workspaceId) => {
      if (applicationId) {
        const app = Configuration.getApplications()[applicationId]
        const document = app?.document || {}
        return Object.keys(document).filter(documentId => {
          const selectedContext = {
            applicationId: applicationId,
            workspaceId: workspaceId,
            trackerTypeId: documentId
          }
          if (Configuration.auth.userCanDoDocumentOperation(selectedContext, 'get')) {
            return documentId
          }
        })
      }
      return []
    },

    getDefaultDocumentId (applicationId, workspaceId) {
      const documentsId = this.getAvailableDocuments(applicationId, workspaceId)
      if (documentsId.length > 0) {
        return documentsId[0]
      }
      return null
    },

    getDocument: (applicationId, documentId) => {
      const app = Configuration.getApplications()[applicationId]
      const document = app?.document || {}
      return document[documentId]
    },

    getDocumentSchema: (applicationId, document) => {
      const app = Configuration.getApplications()[applicationId]
      const schemas = app?.schema || {}
      const schemaId = document?.schema
      if (schemas[schemaId]) {
        return L.merge({}, schemas[schemaId], document.schemaOverrides)
      }
      return undefined
    }
  },

  settings: {
    getAvailableApplicationSettings (applicationId) {
      if (applicationId) {
        const app = Configuration.getApplication(applicationId)
        const settings = app?.settings || {}
        const availableSettings = Object.keys(settings).reduce((acc, settingId) => {
          const setting = settings[settingId]
          setting.id = settingId
          const selectedContext = {
            applicationId: applicationId,
            trackerTypeId: settingId
          }
          if (Configuration.auth.userCanDo(selectedContext, 'get')) {
            setting.tabId = 'tracker'
            acc[settingId] = setting
          } else if (Configuration.auth.userCanDoDocumentOperation(selectedContext, 'get')) {
            setting.tabId = 'document'
            acc[settingId] = setting
          }
          return acc
        }, {})
        return availableSettings
      }
      return {}
    },

    getDefaultApplicationSettings (applicationId) {
      const ApplicationSettings = this.getAvailableApplicationSettings(applicationId)
      if (ApplicationSettings) {
        return Object.values(ApplicationSettings)[0]
      }
      return null
    },

    getAvailableApplicationSettingsId (applicationId) {
      return Object.keys(Configuration.settings.getAvailableApplicationSettings(applicationId))
    }
  }
}
